import { useState, type FormEvent } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import { api } from "../../../data";
import { WebLayout } from "../../../components/shell/WebLayout";
import { authFehler } from "../../konto/auth";
import { CaptchaFeld, captchaConfig } from "../../konto/Captcha";
import { Check } from "./model";
import { MIN_PASSWORD_LENGTH, passwordHint, passwordLengthProblem } from "@talentkontor/domain";
import { t, currentLang, mitSprache } from "../../../lib/sprache";

/** Passwort zurücksetzen (10_website/passwort-zuruecksetzen.html): Link anfordern. Mit ?neu=1 kommt man aus der Mail zurück. */
export function Passwort() {
  const [params] = useSearchParams();
  const neu = !!params.get("neu");
  return (
    <WebLayout title={neu ? t("Neues Passwort festlegen", "Set new password") : t("Passwort zurücksetzen", "Reset password")} schlank={neu}>
      {neu ? <NeuesPasswort /> : <LinkAnfordern />}
    </WebLayout>
  );
}

function LinkAnfordern() {
  // Von „Du hast schon ein Konto“ kommt die Adresse mit (Navigationszustand, nie in der URL)
  const vorgabe = (useLocation().state as { email?: string } | null)?.email;
  const [email, setEmail] = useState(typeof vorgabe === "string" ? vorgabe : "");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaRunde, setCaptchaRunde] = useState(0);
  const mitCaptcha = api.mode !== "demo" && !!captchaConfig();

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const c = new Check();
    const mail = c.email("email", email, { label: t("deine E-Mail-Adresse", "your email address"), required: true });
    setFieldError(c.errors.email ?? null);
    if (!mail) return;
    if (mitCaptcha && !captcha) {
      setError(t("Bitte zuerst die Sicherheitsprüfung lösen.", "Please complete the security check first."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.requestPasswordReset(mail, { captchaToken: captcha, lang: currentLang() });
      setSent(true);
    } catch (err) {
      setError(authFehler(err));
      setCaptchaRunde((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }
  return (
    <main id="main" className="web">
      <form className="centerform panel tk-auth" onSubmit={(e) => void submit(e)} noValidate aria-busy={busy}>
        <div className="tk-auth-intro">
        <h1>{t("Passwort zurücksetzen", "Reset password")}</h1>
        <p>{t("Wenn ein Konto existiert, senden wir einen Link.", "If an account exists, we will send a link.")}</p>
        </div>
        <div className="tk-auth-felder stack">
        <label>
          {t("E-Mail", "Email")}<input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
            aria-invalid={fieldError ? true : undefined} aria-describedby={fieldError ? "pw-email" : undefined} />
          {fieldError && <small id="pw-email" className="tk-error">{fieldError}</small>}
        </label>
        {mitCaptcha && <CaptchaFeld onToken={setCaptcha} resetKey={captchaRunde} />}
        {error && <p className="tk-error" role="alert">{error}</p>}
        {sent && (
          <div className="notice" role="status">
            {api.mode === "demo"
              ? t("Demo: Es wird keine Mail verschickt. In der Demo gibt es keine Passwörter; wähle auf der Anmeldeseite eine Demo-Person.", "Demo: no email is sent. There are no passwords in the demo; choose a demo person on the sign-in page.")
              : t("Wenn zu dieser Adresse ein Konto existiert, ist der Link unterwegs. Prüfe auch den Spam-Ordner.", "If an account exists for this address, the link is on its way. Please also check your spam folder.")}
          </div>
        )}
        <button className="primary" type="submit" disabled={busy}>{busy ? t("Wird angefordert …", "Requesting …") : t("Link anfordern", "Request link")}</button>
        {/* Rückweg in derselben Sprache (SICHT-OEFFENTLICH-BETRIEB-17) */}
        <Link to={mitSprache("/anmelden", currentLang())}>{t("Zurück zur Anmeldung", "Back to sign-in")}</Link>
        </div>
      </form>
    </main>
  );
}

function NeuesPasswort() {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const problem = passwordLengthProblem(pw, currentLang()) ?? (pw !== pw2 ? t("Die beiden Eingaben stimmen nicht überein.", "The two entries do not match.") : null);
    setFieldError(problem);
    if (problem) return;
    setBusy(true);
    setError(null);
    try {
      await api.updatePassword(pw);
      setDone(true);
    } catch (err) {
      setError(authFehler(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <main id="main" className="web">
      <form className="centerform panel tk-auth" onSubmit={(e) => void submit(e)} noValidate aria-busy={busy}>
        <div className="tk-auth-intro">
        <h1>{t("Neues Passwort festlegen", "Set new password")}</h1>
        {/* K-363: nach der Bestätigung der Adresse gilt ein vorab gesetztes Passwort nicht mehr */}
        {typeof window !== "undefined" && new URLSearchParams(window.location.search).get("bestaetigt") && <p>{t("Deine Adresse ist bestätigt. Zum Schutz deines Kontos legst du jetzt einmal dein Passwort fest.", "Your address is confirmed. To protect your account, please set your password once now.")}</p>}
        </div>
        <div className="tk-auth-felder stack">
        {done ? (
          <>
            <div className="notice" role="status">
              {api.mode === "demo" ? t("Demo: Es gibt keine Passwörter. Du bleibst mit deiner Demo-Person angemeldet.", "Demo: there are no passwords. You stay signed in with your demo person.") : t("Dein neues Passwort ist gespeichert.", "Your new password has been saved.")}
            </div>
            <Link className="btn primary" to="/">{t("Weiter zum Portal", "Continue to the portal")}</Link>
          </>
        ) : (
          <>
            <label>
              {t("Neues Passwort", "New password")}<input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" required minLength={MIN_PASSWORD_LENGTH}
                aria-invalid={fieldError ? true : undefined} aria-describedby="pw-neu-hinweis" />
            </label>
            <label>
              {t("Passwort wiederholen", "Repeat password")}<input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" required
                aria-invalid={fieldError ? true : undefined} aria-describedby="pw-neu-hinweis" />
            </label>
            <small id="pw-neu-hinweis" className={fieldError ? "tk-error" : undefined}>{fieldError ?? t(`${passwordHint(currentLang())} Ein Satz aus mehreren Wörtern ist sicher und gut zu merken.`, `${passwordHint(currentLang())} A phrase of several words is secure and easy to remember.`)}</small>
            {error && <p className="tk-error" role="alert">{error}</p>}
            <button className="primary" type="submit" disabled={busy}>{busy ? t("Wird gespeichert …", "Saving …") : t("Passwort speichern", "Save password")}</button>
            <Link to={mitSprache("/passwort", currentLang())}>{t("Neuen Link anfordern", "Request a new link")}</Link>
          </>
        )}
        </div>
      </form>
    </main>
  );
}
